import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import {
  API, TIERS, StatCard, Field, CreditGauge, inputCls,
  Bot, Shield, Plus, Check, CreditCard, Activity, Users, Layers, TrendingUp, Search, ChevronRight, Award, BarChart3
} from './shared';

export default function AgentsTab({ stats }) {
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [showRegister, setShowRegister] = useState(false);
  const [searchAddr, setSearchAddr] = useState('');
  const [regAddr, setRegAddr] = useState('');
  const [regName, setRegName] = useState('');
  const [regDesc, setRegDesc] = useState('');
  const [regCaps, setRegCaps] = useState('');
  const [regStake, setRegStake] = useState('');
  const [regLoading, setRegLoading] = useState(false);
  const [regResult, setRegResult] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/agent-credit/agents`).then(r => r.json()).then(d => setAgents(d.agents || [])).catch(() => {});
  }, []);

  const lookupAgent = async (addr) => {
    const target = addr || searchAddr.trim();
    if (!target) return;
    try {
      const res = await fetch(`${API}/api/agent-credit/agents/${target}`);
      if (res.ok) setSelectedAgent(await res.json());
      else setSelectedAgent({ error: 'Agent not found' });
    } catch { setSelectedAgent({ error: 'Network error' }); }
  };

  const registerAgent = async (e) => {
    e.preventDefault();
    setRegLoading(true);
    setRegResult(null);
    try {
      const res = await fetch(`${API}/api/agent-credit/agents/register`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          agent_address: regAddr.trim(), name: regName.trim(),
          description: regDesc.trim() || null,
          capabilities: regCaps.split(',').map(c => c.trim()).filter(Boolean),
          staked_kutro: parseFloat(regStake) || 0,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setRegResult({ type: 'success', data });
        fetch(`${API}/api/agent-credit/agents`).then(r => r.json()).then(d => setAgents(d.agents || []));
      } else {
        setRegResult({ type: 'error', message: data.detail || 'Registration failed' });
      }
    } catch { setRegResult({ type: 'error', message: 'Network error' }); }
    finally { setRegLoading(false); }
  };

  return (
    <div>
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <StatCard icon={Bot} label="Registered Agents" value={stats.total_registered_agents} color="blue" />
          <StatCard icon={CreditCard} label="Active Credit" value={stats.active_credit_lines} color="green" />
          <StatCard icon={Layers} label="USDC Collateral Staked" value={(stats.total_kutro_staked || 0).toLocaleString()} color="purple" />
          <StatCard icon={TrendingUp} label="Credit Issued" value={`$${(stats.total_credit_issued || 0).toLocaleString()}`} color="orange" />
          <StatCard icon={Activity} label="Agent Revenue" value={`$${(stats.total_agent_revenue || 0).toLocaleString()}`} color="cyan" />
        </div>
      )}

      <div className="flex justify-end mb-4">
        <Button onClick={() => setShowRegister(!showRegister)} className="bg-gradient-to-r from-blue-600 to-purple-600 hover:brightness-110" data-testid="register-agent-btn">
          <Plus className="w-4 h-4 mr-2" /> Register Agent
        </Button>
      </div>

      <AnimatePresence>
        {showRegister && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="mb-8 overflow-hidden">
            <div className="rounded-2xl border border-blue-500/20 bg-blue-500/[0.03] p-6">
              <h3 className="font-semibold text-lg mb-4 flex items-center gap-2"><Shield className="w-5 h-5 text-blue-400" /> Register Agent</h3>
              <form onSubmit={registerAgent} className="grid md:grid-cols-2 gap-4">
                <Field label="Agent Address (ERC-8004)"><input value={regAddr} onChange={e => setRegAddr(e.target.value)} placeholder="0x..." className={inputCls} required data-testid="reg-agent-address" /></Field>
                <Field label="Agent Name"><input value={regName} onChange={e => setRegName(e.target.value)} placeholder="e.g. AlphaTrader-7" className={inputCls.replace('font-mono','')} required data-testid="reg-agent-name" /></Field>
                <Field label="Description"><input value={regDesc} onChange={e => setRegDesc(e.target.value)} placeholder="What does this agent do?" className={inputCls.replace('font-mono','')} data-testid="reg-agent-desc" /></Field>
                <Field label="Capabilities (comma separated)"><input value={regCaps} onChange={e => setRegCaps(e.target.value)} placeholder="trading, research" className={inputCls.replace('font-mono','')} data-testid="reg-agent-caps" /></Field>
                <Field label="Staked USDC Collateral"><input value={regStake} onChange={e => setRegStake(e.target.value)} placeholder="0" type="number" min="0" className={inputCls} data-testid="reg-agent-stake" /></Field>
                <div className="flex items-end">
                  <Button type="submit" disabled={regLoading} className="w-full h-12 bg-gradient-to-r from-blue-600 to-purple-600" data-testid="reg-submit-btn">
                    {regLoading ? 'Registering...' : 'Register Agent'}
                  </Button>
                </div>
              </form>
              {regResult && (
                <div className={`mt-4 p-4 rounded-lg border ${regResult.type === 'success' ? 'bg-green-500/10 border-green-500/20' : 'bg-red-500/10 border-red-500/20'}`}>
                  {regResult.type === 'success' ? (
                    <div><p className="text-sm text-green-400 flex items-center gap-2"><Check className="w-4 h-4" /> Agent registered!</p>
                      <p className="text-xs text-gray-500 mt-1 font-mono">ACS: {regResult.data.initial_score.acs} | Tier: {regResult.data.initial_score.tier} | Max Credit: ${regResult.data.initial_score.max_credit_line.toLocaleString()}</p></div>
                  ) : (<p className="text-sm text-red-400">{regResult.message}</p>)}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" />
              <input value={searchAddr} onChange={e => setSearchAddr(e.target.value)} onKeyDown={e => e.key === 'Enter' && lookupAgent()}
                placeholder="Look up agent by address (0x...)" className={`${inputCls} pl-10`} data-testid="agent-search-input" />
            </div>
            <Button onClick={() => lookupAgent()} variant="outline" className="border-white/10" data-testid="agent-search-btn">SCAN</Button>
          </div>

          {selectedAgent && !selectedAgent.error && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-testid="agent-detail-card">
              <div className="flex flex-col md:flex-row gap-6">
                <CreditGauge score={selectedAgent.credit_score.acs} tier={selectedAgent.credit_score.tier} />
                <div className="flex-1">
                  <h3 className="text-xl font-bold">{selectedAgent.name}</h3>
                  <code className="text-xs text-gray-500 font-mono">{selectedAgent.agent_address}</code>
                  {selectedAgent.description && <p className="text-sm text-gray-400 mt-2">{selectedAgent.description}</p>}
                  <div className="flex flex-wrap gap-2 mt-3">
                    {(selectedAgent.capabilities || []).map((cap, i) => (
                      <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400">{cap}</span>
                    ))}
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
                    <div className="text-center p-2 rounded-lg bg-white/[0.03]"><div className="text-lg font-mono font-bold">{selectedAgent.credit_score.data_points.total_evaluations}</div><div className="text-[10px] text-gray-500 uppercase">Evaluations</div></div>
                    <div className="text-center p-2 rounded-lg bg-white/[0.03]"><div className="text-lg font-mono font-bold text-green-400">{selectedAgent.credit_score.data_points.accepted_tasks}</div><div className="text-[10px] text-gray-500 uppercase">Accepted</div></div>
                    <div className="text-center p-2 rounded-lg bg-white/[0.03]"><div className="text-lg font-mono font-bold">{selectedAgent.staked_kutro.toLocaleString()}</div><div className="text-[10px] text-gray-500 uppercase">Staked</div></div>
                    <div className="text-center p-2 rounded-lg bg-white/[0.03]"><div className="text-lg font-mono font-bold">${selectedAgent.credit_score.max_credit_line.toLocaleString()}</div><div className="text-[10px] text-gray-500 uppercase">Credit Line</div></div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
          {selectedAgent?.error && <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-sm text-red-400">{selectedAgent.error}</div>}

          <div className="rounded-2xl border border-white/5 bg-white/[0.02] overflow-hidden" data-testid="agent-registry-table">
            <div className="px-5 py-4 border-b border-white/5"><h3 className="font-semibold flex items-center gap-2"><Users className="w-4 h-4 text-blue-400" /> Agent Registry</h3></div>
            {agents.length > 0 ? (
              <div className="divide-y divide-white/[0.03]">
                {agents.map(agent => {
                  const t = TIERS[agent.tier] || TIERS.Restricted;
                  return (
                    <div key={agent.agent_address} className="px-5 py-3.5 flex items-center justify-between hover:bg-white/[0.02] transition-colors cursor-pointer"
                      onClick={() => lookupAgent(agent.agent_address)}>
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-lg ${t.bg} flex items-center justify-center`}><Bot className={`w-4 h-4 ${t.text}`} /></div>
                        <div><div className="text-sm font-medium">{agent.name}</div><code className="text-[10px] text-gray-600 font-mono">{agent.agent_address.slice(0, 10)}...{agent.agent_address.slice(-6)}</code></div>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className={`text-xs font-mono px-2 py-0.5 rounded-full ${t.bg} ${t.border} border`}>ACS {agent.acs || '—'}</span>
                        <span className={`text-xs font-mono ${t.border} border px-2 py-0.5 rounded-full`}>{agent.tier || '—'}</span>
                        <ChevronRight className="w-4 h-4 text-gray-600" />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="py-12 text-center"><Bot className="w-8 h-8 text-gray-700 mx-auto mb-3" /><p className="text-sm text-gray-600">No agents registered yet.</p></div>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div className="rounded-2xl border border-white/5 bg-white/[0.02] p-5" data-testid="credit-tiers">
            <h3 className="font-semibold mb-4 flex items-center gap-2"><Award className="w-4 h-4 text-orange-400" /> Credit Tiers</h3>
            <div className="space-y-3">
              {[
                { tier: 'Sovereign', range: '750-850', credit: '$100K', rate: '3.5%' },
                { tier: 'Autonomous', range: '650-749', credit: '$25K', rate: '6.0%' },
                { tier: 'Supervised', range: '550-649', credit: '$5K', rate: '9.5%' },
                { tier: 'Provisional', range: '450-549', credit: '$1K', rate: '14.0%' },
                { tier: 'Restricted', range: '300-449', credit: '$0', rate: '—' },
              ].map(t => {
                const s = TIERS[t.tier];
                return (
                  <div key={t.tier} className={`p-3 rounded-lg border ${s.border} ${s.bg}`}>
                    <div className="flex justify-between items-center"><span className="text-sm font-semibold">{t.tier}</span><span className="text-xs font-mono text-gray-400">ACS {t.range}</span></div>
                    <div className="flex justify-between mt-1"><span className="text-xs text-gray-500">Max: {t.credit}</span><span className="text-xs text-gray-500">APR: {t.rate}</span></div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="rounded-2xl border border-white/5 bg-white/[0.02] p-5">
            <h3 className="font-semibold mb-4 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-purple-400" /> ACS Scoring Weights</h3>
            <div className="space-y-2">
              {[
                { label: 'Task Performance', weight: '40%', c: 'text-blue-400' },
                { label: 'Revenue History', weight: '20%', c: 'text-green-400' },
                { label: 'Staked USDC Collateral', weight: '15%', c: 'text-purple-400' },
                { label: 'Counterparty Diversity', weight: '10%', c: 'text-cyan-400' },
                { label: 'Uptime', weight: '5%', c: 'text-gray-400' },
                { label: 'Dispute Penalty', weight: '-10%', c: 'text-red-400' },
              ].map(s => (
                <div key={s.label} className="flex justify-between items-center py-1.5">
                  <span className="text-xs text-gray-400">{s.label}</span><span className={`text-xs font-mono ${s.c}`}>{s.weight}</span>
                </div>
              ))}
            </div>
          </div>
          <a href="/kojiru-agent-credit-whitepaper.md" target="_blank" className="block rounded-2xl border border-white/5 bg-white/[0.02] p-5 hover:border-white/10 transition-colors" data-testid="whitepaper-link">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center"><Layers className="w-5 h-5" /></div>
              <div><h4 className="font-semibold text-sm">Agent Credit Whitepaper</h4><p className="text-xs text-gray-500">Full protocol specification</p></div>
              <ChevronRight className="w-4 h-4 text-gray-600 ml-auto" />
            </div>
          </a>
        </div>
      </div>
    </div>
  );
}
